import React from 'react';
import { useMediaQuery } from '@mui/material';

import iconClose from 'public/images/close-bold.svg';
import iconArrow from 'public/images/arrow-button.svg';
import Image from 'next/legacy/image';
import ShareButtonV2 from 'componentsV2/ShareButtonV2';
import PrintButtonV2 from 'componentsV2/PrintButtonV2';

export type GalleryPopupHeaderV2Props = {
  name: string;
  stats: React.ReactNode[];
  onClose?: () => void;
  primaryAddress?: string;
  // Large photo viewer: phone shows a compact back / counter / save / share row
  photoCounter?: string;
  favoriteButton?: (
    showLabel: boolean,
    useLargeIcons: boolean
  ) => React.ReactNode;
};

const noLigatures = { fontFeatureSettings: "'liga' off, 'clig' off" };
const iconHoverClass = 'flex hover-animation hover:opacity-70';

type IconSize = { h: number; w: number };

// Print · Save · Share, in the design's order
const HeaderActions: React.FC<{
  favoriteButton: GalleryPopupHeaderV2Props['favoriteButton'];
  showLabels: boolean;
  useLargeIcons: boolean;
  printSize: IconSize;
  shareSize: IconSize;
  labelClassName?: string;
  gapClassName?: string;
  itemClassName: string;
}> = ({
  favoriteButton,
  showLabels,
  useLargeIcons,
  printSize,
  shareSize,
  labelClassName,
  gapClassName,
  itemClassName,
}) => (
  <>
    <span className={itemClassName}>
      <PrintButtonV2
        h={printSize.h}
        w={printSize.w}
        labelClassName={labelClassName}
        gapClassName={gapClassName}
      />
    </span>
    <span className={itemClassName}>
      {favoriteButton?.(showLabels, useLargeIcons)}
    </span>
    <span className={itemClassName}>
      <ShareButtonV2
        h={shareSize.h}
        w={shareSize.w}
        labelClassName={labelClassName}
        gapClassName={gapClassName}
      />
    </span>
  </>
);

const GalleryPopupHeaderV2: React.FC<GalleryPopupHeaderV2Props> = ({
  name,
  stats,
  onClose,
  primaryAddress = '',
  photoCounter,
  favoriteButton,
}) => {
  const showIconLabels = useMediaQuery('(min-width:800px)');
  const useLargeIcons = useMediaQuery(
    '(min-width:768px) and (max-width:799.98px)'
  );
  const printIconSize = useLargeIcons ? { h: 22, w: 28 } : { h: 14, w: 18 };
  const shareIconSize = useLargeIcons ? { h: 23, w: 19 } : { h: 14, w: 18 };

  return (
    <div className="relative">
      {/* Desktop */}
      <div className="hidden md:flex md:flex-col md:gap-[5px] max-w-[1368px] mx-auto w-full md:px-[40px] md:py-[15px]">
        <div className="flex items-start gap-x-[15px] w-full">
          <div className="flex flex-1 flex-wrap items-center justify-between gap-y-[10px] min-w-[0px]">
            <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[6px] min-w-[0px]">
              <h1 className="text-[21px] leading-[23px] text-black m-0 font-sohneKraftig border-r border-gray-border pr-[10px] break-words shrink min-w-[0px] no-liga">
                {name}
              </h1>
              <ul className="flex flex-wrap items-center gap-x-[10px] gap-y-[4px]">
                {stats.map((stat, idx) => (
                  <li
                    key={idx}
                    className="text-16-21-0.3 text-black font-sohneBuch whitespace-nowrap shrink-0 no-liga"
                  >
                    {stat}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex items-center pt-[3px] gap-[15px] shrink-0">
              <HeaderActions
                favoriteButton={favoriteButton}
                showLabels={showIconLabels}
                useLargeIcons={useLargeIcons}
                printSize={printIconSize}
                shareSize={shareIconSize}
                labelClassName={
                  showIconLabels ? 'text-14-18-0.3 font-sohneBuch' : '!hidden'
                }
                gapClassName="gap-[4px]"
                itemClassName={iconHoverClass}
              />
            </div>
          </div>
          {onClose && (
            <div className="flex pt-[3px] shrink-0">
              <button
                type="button"
                className={`flex items-center h-[28px] ${iconHoverClass}`}
                onClick={onClose}
              >
                <Image
                  src={iconClose}
                  alt="close popup"
                  width={25}
                  height={25}
                />
              </button>
            </div>
          )}
        </div>
        {primaryAddress && (
          <p
            className="text-12-18-0.3 text-black m-0 font-sohneBuch"
            style={noLigatures}
          >
            {primaryAddress}
          </p>
        )}
      </div>

      {/* Mobile */}
      {photoCounter ? (
        <div className="relative flex md:hidden items-center justify-between w-full pt-[15px] pb-[15px] px-[20px]">
          {onClose && (
            <button
              type="button"
              className="flex"
              onClick={onClose}
              aria-label="Back to all photos"
            >
              <span className="flex transform rotate-180">
                <Image src={iconArrow} alt="" width={26} height={20} />
              </span>
            </button>
          )}
          {/* Centered on the row, not between the uneven side groups */}
          <span
            className="absolute left-1/2 -translate-x-1/2 pointer-events-none text-16-22-0.3 text-black m-0 font-sohneKraftig whitespace-nowrap"
            style={noLigatures}
          >
            {photoCounter}
          </span>
          <div className="flex items-center gap-[15px] h-[22px]">
            {favoriteButton?.(false, false)}
            <ShareButtonV2 h={23} w={19} />
          </div>
        </div>
      ) : (
        <div className="flex md:hidden flex-col items-start gap-[10px] w-full pt-[15px] pb-[15px] px-[20px]">
          <div className="flex flex-col gap-[3px] w-full">
            <div className="flex items-center justify-between w-full">
              <h1
                className="text-16-22-0.3 text-black m-0 font-sohneKraftig"
                style={noLigatures}
              >
                {name}
              </h1>
              {onClose && (
                <button type="button" onClick={onClose}>
                  <Image
                    src={iconClose}
                    alt="close popup"
                    width={21}
                    height={21}
                  />
                </button>
              )}
            </div>
            {primaryAddress && (
              <p
                className="text-12-18-0.3 text-black m-0 font-sohneBuch"
                style={noLigatures}
              >
                {primaryAddress}
              </p>
            )}
          </div>
          <div className="flex items-center gap-[15px]">
            <HeaderActions
              favoriteButton={favoriteButton}
              showLabels={false}
              useLargeIcons={false}
              printSize={{ h: 22, w: 28 }}
              shareSize={{ h: 23, w: 19 }}
              itemClassName="flex"
            />
          </div>
        </div>
      )}
      <div className="hidden md:block w-full h-px bg-gray-border" />
    </div>
  );
};

export default GalleryPopupHeaderV2;
